/**
 * Navigation Configuration for Role-Based Access Control
 * Host Me Property Management System
 *
 * This file defines the navigation structure for different user roles:
 * - admin: Full system access
 * - turnKeyOwner: Property management with some admin features
 * - serviceClient: No sidebar nav (standalone client dashboard at /client/t/:token)
 */

export const getNavigationConfig = (userRole) => {
  const navigationMap = {
    admin: [
      {
        id: 'overview',
        label: 'نظرة عامة',
        icon: 'bi-bar-chart',
        path: '/dashboard',
        description: 'System overview and statistics'
      },
      {
        id: 'ops-performance',
        label: 'مؤشر أداء العمليات',
        icon: 'bi-speedometer2',
        path: '/ops/performance',
        description: 'Operations performance scorecard'
      },
      {
        id: 'guest-reviews',
        label: 'التقييمات والشكاوى',
        icon: 'bi-star-half',
        path: '/ops/reviews',
        description: 'تقييم الضيوف المغادرين، والرد على التقييمات، وشكاوى الضيوف',
        badge: 'guestReviews'
      },
      {
        id: 'properties',
        label: 'إدارة الوحدات',
        icon: 'bi-house',
        path: '/properties',
        description: 'All properties management'
      },
      {
        id: 'bookings',
        label: 'إدارة الحجوزات',
        icon: 'bi-calendar3',
        path: '/bookings',
        description: 'All bookings management'
      },
      {
        id: 'units-calendar',
        label: 'التقويم',
        icon: 'bi-calendar-week',
        path: '/ops/calendar',
        description: 'توفر كل الوحدات في صفحة واحدة'
      },
      {
        // Core staff only — guest message content is out of scope for owners (stage 1), who are
        // 403'd at the API as well as having no nav entry.
        id: 'inbox',
        label: 'صندوق الرسائل',
        icon: 'bi-chat-dots',
        path: '/inbox',
        description: 'Unified guest inbox — read-only (Guest Comms stage 1, Inc 3)'
      },
      {
        id: 'stock',
        label: 'المخزون',
        icon: 'bi-box-seam',
        path: '/ops/stock',
        description: 'مستلزمات الضيافة في المخزن: الرصيد والتسليم والجرد'
      },
      {
        // «أداء الوحدات» — the weekly listing-performance record (tasks/property-performance-phase1.md, D2).
        id: 'unit-performance',
        label: 'أداء الوحدات',
        icon: 'bi-graph-up',
        path: '/ops/unit-performance',
        description: 'القراءة الأسبوعية لكل وحدة على Airbnb وBooking.com وسجل التغييرات'
      },
      {
        id: 'alerts',
        label: 'التنبيهات',
        icon: 'bi-bell',
        path: '/alerts',
        description: 'Alert triage (booking conflicts, sync errors)'
      },
      {
        id: 'tasks',
        label: 'إدارة المهام',
        icon: 'bi-list-task',
        path: '/tasks',
        description: 'Task management center'
      },
      {
        id: 'guest-requests',
        label: 'طلبات الضيوف',
        icon: 'bi-inbox',
        path: '/guest-requests',
        description: 'Guest Requests Hub — global open-requests inbox (V1.1)'
      },
      {
        id: 'guests',
        label: 'إدارة الضيوف',
        icon: 'bi-people',
        path: '/guests',
        description: 'Guest management'
      },
      // Hidden 2026-04-21: reverting to Channex-as-inbox. Messages performance
      // now lives on the Operations Performance Dashboard (the old admin-dashboard
      // UnansweredMessagesCard was removed 2026-07-19). Route /messages still
      // resolves for direct URL access. Restore when two-way sync is shipped.
      /* {
        id: 'messages',
        label: 'صندوق الرسائل الموحد',
        icon: 'bi-envelope',
        path: '/messages',
        description: 'Unified messaging inbox'
      }, */
      {
        id: 'messaging-automation',
        label: 'أتمتة الرسائل',
        icon: 'bi-robot',
        path: '/messaging-automation',
        description: 'Automated messaging system'
      },
      // Hidden 2026-05-01: AI assistant not production-ready. Routes still
      // resolve for direct URL access (/admin/agent-trace, /admin/live-conversations).
      // Restore when AI roadmap resumes (paused per memory project_ai_paused_2026).
      /* {
        id: 'agent-trace',
        label: ' تتبع المساعد الذكي (المشرف)',
        icon: 'bi-activity',
        path: '/admin/agent-trace',
        description: 'Monitor AI assistant activity and logs'
      }, */
      // Hidden 2026-05-01: AI assistant not production-ready. Route still
      // resolves for direct URL access (/admin/live-conversations).
      // Restore when AI roadmap resumes (paused per memory project_ai_paused_2026).
      /* {
        id: 'live-conversations',
        label: 'المحادثات المباشرة',
        icon: 'bi-chat-dots',
        path: '/admin/live-conversations',
        description: 'Monitor and join active AI conversations'
      }, */
      // {
      //   id: 'intent-policies',
      //   label: 'سياسات النوايا',
      //   icon: 'bi-sliders',
      //   path: '/admin/intent-policies',
      //   description: 'Configure AI intent monitoring policies'
      // },
      {
        id: 'finance',
        label: 'الإدارة المالية',
        icon: 'bi-cash-stack',
        path: '/finance',
        description: 'Financial reports and management'
      },
      {
        id: 'billing',
        label: 'كشف رسوم الإدارة',
        icon: 'bi-receipt',
        path: '/billing',
        description: 'Owner commission billing worksheet — internal accounting'
      },
      {
        id: 'services',
        label: 'خدمات النظافة',
        icon: 'bi-bucket',
        path: '/services',
        description: 'Cleaning service management'
      },
      {
        id: 'events',
        label: 'الفعاليات المحلية',
        icon: 'bi-calendar-event',
        path: '/events',
        description: 'Local events management'
      },
      {
        id: 'pricing-assistant',
        label: 'مساعد التسعير',
        icon: 'bi-calculator',
        path: '/pricing-assistant',
        description: 'What a unit achieves, and what to quote — guidance, never a suggested price'
      },
      {
        // Founder, 13 Sep: account admin sits at the bottom, just above settings.
        id: 'users',
        label: 'إدارة المستخدمين',
        icon: 'bi-people',
        path: '/admin/users',
        description: 'User account management (v3.0)'
      },
      {
        id: 'settings',
        label: 'الإعدادات',
        icon: 'bi-gear',
        path: '/settings',
        description: 'System settings and configuration'
      }
    ],

    ops: [
      {
        id: 'overview',
        label: 'نظرة عامة',
        icon: 'bi-bar-chart',
        path: '/dashboard',
        description: 'Operations overview'
      },
      {
        id: 'ops-performance',
        label: 'مؤشر أداء العمليات',
        icon: 'bi-speedometer2',
        path: '/ops/performance',
        description: 'Operations performance scorecard'
      },
      {
        id: 'guest-reviews',
        label: 'التقييمات والشكاوى',
        icon: 'bi-star-half',
        path: '/ops/reviews',
        description: 'تقييم الضيوف المغادرين، والرد على التقييمات، وشكاوى الضيوف',
        badge: 'guestReviews'
      },
      {
        id: 'properties',
        label: 'إدارة العقارات',
        icon: 'bi-house',
        path: '/properties',
        description: 'All properties management'
      },
      {
        id: 'bookings',
        label: 'إدارة الحجوزات',
        icon: 'bi-calendar3',
        path: '/bookings',
        description: 'All bookings management'
      },
      {
        id: 'units-calendar',
        label: 'التقويم',
        icon: 'bi-calendar-week',
        path: '/ops/calendar',
        description: 'توفر كل الوحدات في صفحة واحدة'
      },
      {
        // Core staff only — guest message content is out of scope for owners (stage 1), who are
        // 403'd at the API as well as having no nav entry.
        id: 'inbox',
        label: 'صندوق الرسائل',
        icon: 'bi-chat-dots',
        path: '/inbox',
        description: 'Unified guest inbox — read-only (Guest Comms stage 1, Inc 3)'
      },
      {
        id: 'stock',
        label: 'المخزون',
        icon: 'bi-box-seam',
        path: '/ops/stock',
        description: 'مستلزمات الضيافة في المخزن: الرصيد والتسليم والجرد'
      },
      {
        // «أداء الوحدات» — the weekly listing-performance record (tasks/property-performance-phase1.md, D2).
        id: 'unit-performance',
        label: 'أداء الوحدات',
        icon: 'bi-graph-up',
        path: '/ops/unit-performance',
        description: 'القراءة الأسبوعية لكل وحدة على Airbnb وBooking.com وسجل التغييرات'
      },
      {
        id: 'alerts',
        label: 'التنبيهات',
        icon: 'bi-bell',
        path: '/alerts',
        description: 'Alert triage (booking conflicts, sync errors)'
      },
      {
        id: 'tasks',
        label: 'إدارة المهام',
        icon: 'bi-list-task',
        path: '/tasks',
        description: 'Task management center'
      },
      {
        id: 'guest-requests',
        label: 'طلبات الضيوف',
        icon: 'bi-inbox',
        path: '/guest-requests',
        description: 'Guest Requests Hub — global open-requests inbox (V1.1)'
      },
      {
        id: 'guests',
        label: 'إدارة الضيوف',
        icon: 'bi-people',
        path: '/guests',
        description: 'Guest management'
      },
      // Hidden 2026-04-21: reverting to Channex-as-inbox. Messages performance
      // now lives on the Operations Performance Dashboard (the old admin-dashboard
      // UnansweredMessagesCard was removed 2026-07-19). Route /messages still
      // resolves for direct URL access. Restore when two-way sync is shipped.
      /* {
        id: 'messages',
        label: 'صندوق الرسائل الموحد',
        icon: 'bi-envelope',
        path: '/messages',
        description: 'Unified messaging inbox'
      }, */
      {
        id: 'finance',
        label: 'الإدارة المالية',
        icon: 'bi-cash-stack',
        path: '/finance',
        description: 'Financial reports and management'
      },
      {
        id: 'billing',
        label: 'كشف رسوم الإدارة',
        icon: 'bi-receipt',
        path: '/billing',
        description: 'Owner commission billing worksheet — internal accounting'
      },
      {
        id: 'services',
        label: 'خدمات النظافة',
        icon: 'bi-bucket',
        path: '/services',
        description: 'Cleaning service management'
      },
      {
        id: 'events',
        label: 'الفعاليات المحلية',
        icon: 'bi-calendar-event',
        path: '/events',
        description: 'Local events management'
      },
      {
        id: 'pricing-assistant',
        label: 'مساعد التسعير',
        icon: 'bi-calculator',
        path: '/pricing-assistant',
        description: 'What a unit achieves, and what to quote — guidance, never a suggested price'
      },
      {
        id: 'settings',
        label: 'الإعدادات',
        icon: 'bi-gear',
        path: '/settings',
        description: 'Account settings'
      }
    ],

    'ops-manager': [
      {
        id: 'overview',
        label: 'نظرة عامة',
        icon: 'bi-bar-chart',
        path: '/dashboard',
        description: 'Operations overview'
      },
      {
        id: 'ops-performance',
        label: 'مؤشر أداء العمليات',
        icon: 'bi-speedometer2',
        path: '/ops/performance',
        description: 'Operations performance scorecard'
      },
      {
        id: 'guest-reviews',
        label: 'التقييمات والشكاوى',
        icon: 'bi-star-half',
        path: '/ops/reviews',
        description: 'تقييم الضيوف المغادرين، والرد على التقييمات، وشكاوى الضيوف',
        badge: 'guestReviews'
      },
      // Hidden 2026-05-01: AI assistant not production-ready. Route still
      // resolves for direct URL access (/admin/live-conversations).
      // Restore when AI roadmap resumes (paused per memory project_ai_paused_2026).
      /* {
        id: 'live-conversations',
        label: 'المحادثات المباشرة',
        icon: 'bi-chat-dots',
        path: '/admin/live-conversations',
        description: 'Monitor and join active AI conversations'
      }, */
      {
        id: 'tasks',
        label: 'إدارة المهام',
        icon: 'bi-list-task',
        path: '/tasks',
        description: 'Task management center'
      },
      {
        id: 'guest-requests',
        label: 'طلبات الضيوف',
        icon: 'bi-inbox',
        path: '/guest-requests',
        description: 'Guest Requests Hub — global open-requests inbox (V1.1)'
      },
      {
        id: 'properties',
        label: 'إدارة العقارات',
        icon: 'bi-house',
        path: '/properties',
        description: 'All properties management'
      },
      {
        id: 'bookings',
        label: 'إدارة الحجوزات',
        icon: 'bi-calendar3',
        path: '/bookings',
        description: 'All bookings management'
      },
      {
        id: 'units-calendar',
        label: 'التقويم',
        icon: 'bi-calendar-week',
        path: '/ops/calendar',
        description: 'توفر كل الوحدات في صفحة واحدة'
      },
      {
        // Core staff only — guest message content is out of scope for owners (stage 1), who are
        // 403'd at the API as well as having no nav entry.
        id: 'inbox',
        label: 'صندوق الرسائل',
        icon: 'bi-chat-dots',
        path: '/inbox',
        description: 'Unified guest inbox — read-only (Guest Comms stage 1, Inc 3)'
      },
      {
        id: 'stock',
        label: 'المخزون',
        icon: 'bi-box-seam',
        path: '/ops/stock',
        description: 'مستلزمات الضيافة في المخزن: الرصيد والتسليم والجرد'
      },
      {
        // «أداء الوحدات» — the weekly listing-performance record (tasks/property-performance-phase1.md, D2).
        id: 'unit-performance',
        label: 'أداء الوحدات',
        icon: 'bi-graph-up',
        path: '/ops/unit-performance',
        description: 'القراءة الأسبوعية لكل وحدة على Airbnb وBooking.com وسجل التغييرات'
      },
      {
        id: 'alerts',
        label: 'التنبيهات',
        icon: 'bi-bell',
        path: '/alerts',
        description: 'Alert triage (booking conflicts, sync errors)'
      },
      {
        id: 'guests',
        label: 'إدارة الضيوف',
        icon: 'bi-people',
        path: '/guests',
        description: 'Guest management'
      },
      // Hidden 2026-04-21: reverting to Channex-as-inbox. Messages performance
      // now lives on the Operations Performance Dashboard (the old admin-dashboard
      // UnansweredMessagesCard was removed 2026-07-19). Route /messages still
      // resolves for direct URL access. Restore when two-way sync is shipped.
      /* {
        id: 'messages',
        label: 'صندوق الرسائل الموحد',
        icon: 'bi-envelope',
        path: '/messages',
        description: 'Unified messaging inbox'
      }, */
      {
        id: 'services',
        label: 'خدمات النظافة',
        icon: 'bi-bucket',
        path: '/services',
        description: 'Cleaning service management'
      },
      {
        id: 'events',
        label: 'الفعاليات المحلية',
        icon: 'bi-calendar-event',
        path: '/events',
        description: 'Local events management'
      },
      {
        id: 'billing',
        label: 'كشف رسوم الإدارة',
        icon: 'bi-receipt',
        path: '/billing',
        description: 'Owner commission billing worksheet — internal accounting'
      },
      {
        id: 'settings',
        label: 'الإعدادات',
        icon: 'bi-gear',
        path: '/settings',
        description: 'System settings and configuration'
      }
    ],

    turnKeyOwner: [
      {
        id: 'overview',
        label: 'نظرة عامة',
        icon: 'bi-bar-chart',
        path: '/dashboard',
        description: 'Property performance overview'
      },
      {
        id: 'properties',
        label: 'إدارة العقارات',
        icon: 'bi-house',
        path: '/properties',
        description: 'My properties management'
      },
      {
        id: 'bookings',
        label: 'إدارة الحجوزات',
        icon: 'bi-calendar3',
        path: '/bookings',
        description: 'My properties bookings'
      },
      // No guests or guest requests: owners do not see them (8c batch 3). Owners now get the owner pages' own
      // menu (owner/shell/OwnerShell.js); this entry survives as the fallback for an unknown role (below).
      {
        id: 'finance',
        label: 'الإدارة المالية',
        icon: 'bi-cash-stack',
        path: '/finance',
        description: 'Financial reports'
      },
      {
        id: 'settings',
        label: 'الإعدادات',
        icon: 'bi-gear',
        path: '/settings',
        description: 'Account settings'
      }
    ],
    
    'maintenance-supervisor': [
      {
        id: 'tasks',
        label: 'إدارة المهام',
        icon: 'bi-list-task',
        path: '/tasks',
        description: 'Task management and assignment'
      },
      {
        id: 'my-tasks',
        label: 'مهامي',
        icon: 'bi-clipboard-check',
        path: '/my-tasks',
        description: 'Execute assigned tasks with feedback'
      },
      {
        id: 'properties',
        label: 'العقارات',
        icon: 'bi-house',
        path: '/properties',
        description: 'Properties (read-only)'
      },
      {
        id: 'guests',
        label: 'الضيوف',
        icon: 'bi-people',
        path: '/guests',
        description: 'Guest management'
      },
      {
        id: 'services',
        label: 'خدمات النظافة',
        icon: 'bi-bucket',
        path: '/services',
        description: 'Cleaning service management'
      },
      {
        id: 'finance',
        label: 'الإدارة المالية',
        icon: 'bi-cash-stack',
        path: '/finance',
        description: 'Financial reports'
      },
      {
        id: 'settings',
        label: 'الإعدادات',
        icon: 'bi-gear',
        path: '/settings',
        description: 'Account settings'
      }
    ],

    maintenance: [
      {
        id: 'my-tasks',
        label: 'المهام',
        icon: 'bi-list-task',
        path: '/my-tasks',
        description: 'Assigned work orders'
      },
      {
        id: 'properties',
        label: 'العقارات',
        icon: 'bi-house',
        path: '/properties',
        description: 'Properties (read-only)'
      },
      {
        id: 'settings',
        label: 'الإعدادات',
        icon: 'bi-gear',
        path: '/settings',
        description: 'Account settings'
      }
    ],

    serviceClient: [],

    // External accountant — exactly one page, and nothing else is navigable.
    // ⚠️ This entry is load-bearing: without it the fallback below serves an accountant
    // the OWNER's sidebar (properties, bookings, guests, finance), every link of which
    // they may not open.
    accountant: [
      {
        id: 'billing',
        label: 'كشف رسوم الإدارة',
        icon: 'bi-receipt',
        path: '/billing',
        description: 'Owner management-fee worksheet — the accountant\'s only working page'
      },
      {
        // Their OWN account only — last login, change password, preferences. Nothing
        // systemic lives there. Included because we hand this role a password and they
        // must be able to change it without asking us.
        id: 'settings',
        label: 'الإعدادات',
        icon: 'bi-gear',
        path: '/settings',
        description: 'Own profile and password'
      }
    ]
  };

  // Falls back to the owner menu for an unrecognised role. ⚠️ Any role added to the
  // backend enum before its frontend lands inherits an owner sidebar by default — give
  // it an explicit entry above, as `accountant` has.
  return navigationMap[userRole] || navigationMap.turnKeyOwner;
};

/**
 * Get user role display name in Arabic
 */
export const getRoleDisplayName = (role) => {
  const roleNames = {
    admin: 'مدير',
    ops: 'موظف عمليات',
    'ops-manager': 'مدير عمليات',
    'maintenance-supervisor': 'مشرف صيانة',
    maintenance: 'فريق الصيانة',
    turnKeyOwner: 'مالك - عضوية خدمة شاملة',
    serviceClient: 'عميل خدمات',
    service: 'خدمات',
    accountant: 'محاسب'
  };

  return roleNames[role] || 'مستخدم';
};

/**
 * Check if user has access to specific navigation item
 */
export const hasAccessToNavItem = (userRole, navItemId) => {
  const userNavigation = getNavigationConfig(userRole);
  return userNavigation.some(item => item.id === navItemId);
};

/**
 * Get dashboard title based on user role
 */
export const getDashboardTitle = (role) => {
  const titles = {
    admin: 'لوحة تحكم المدير',
    ops: 'لوحة تحكم العمليات',
    'ops-manager': 'لوحة تحكم مدير العمليات',
    'maintenance-supervisor': 'لوحة تحكم مشرف الصيانة',
    maintenance: 'قائمة المهام',
    turnKeyOwner: 'لوحة تحكم المالك',
    serviceClient: 'لوحة تحكم العميل',
    accountant: 'كشف رسوم الإدارة'
  };

  return titles[role] || 'لوحة التحكم';
};
